import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { PasswordStrengthMeter, MIN_PASSWORD_LENGTH } from "@/components/bi/PasswordStrengthMeter";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [pwOk, setPwOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < MIN_PASSWORD_LENGTH) { toast.error(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`); return; }
    if (!pwOk) { toast.error("Please choose a stronger password"); return; }
    setLoading(true);
    try {
      await signup(name, email, password);
      toast.success("Account created!");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex bi-canvas">
      <div className="hidden lg:flex flex-col justify-between w-[46%] hero-gradient p-12">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-[color:var(--brand-ocean)] flex items-center justify-center"><Sparkles className="w-5 h-5 text-white" /></div>
          <span className="font-display text-lg font-semibold text-[color:var(--ink)]">Eazy Analytics</span>
        </div>
        <div>
          <h1 className="font-display text-4xl xl:text-5xl tracking-tight text-[color:var(--ink)] leading-[1.1]">Start building<br/>in minutes.</h1>
          <p className="text-[color:var(--ink-2)] mt-4 max-w-md">Your data, beautifully visualized. No setup, no friction.</p>
        </div>
        <div className="text-xs text-[color:var(--ink-3)]">© Eazy Analytics</div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <h2 className="font-display text-2xl text-[color:var(--ink)]">Create account</h2>
          <p className="text-sm text-[color:var(--ink-3)] mt-1">Free to get started.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div><Label className="text-[color:var(--ink-2)]">Name</Label><Input data-testid="signup-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 bg-[color:var(--surface)]" required /></div>
            <div><Label className="text-[color:var(--ink-2)]">Email</Label><Input data-testid="signup-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 bg-[color:var(--surface)]" required /></div>
            <div><Label className="text-[color:var(--ink-2)]">Password</Label>
              <div className="relative mt-1.5">
                <Input data-testid="signup-password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} className="bg-[color:var(--surface)] pr-10" required minLength={MIN_PASSWORD_LENGTH} />
                <button type="button" data-testid="signup-password-toggle" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"} className="absolute inset-y-0 right-0 flex items-center px-3 text-[color:var(--ink-3)] hover:text-[color:var(--ink)]">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <PasswordStrengthMeter password={password} userInputs={[name, email]} onChange={(s) => setPwOk(s.valid)} />
            </div>
            <Button data-testid="signup-submit" type="submit" disabled={loading || (password.length > 0 && !pwOk)} className="w-full bg-[color:var(--brand-ocean)] hover:bg-[#1E3AD9] text-white">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
            </Button>
          </form>
          <p className="text-sm text-[color:var(--ink-3)] mt-5 text-center">Already have an account? <Link to="/login" className="text-[color:var(--brand-ocean)] font-medium">Sign in</Link></p>
        </div>
      </div>
    </div>
  );
}
